// src/components/proposal/LaborSummaryTable.tsx
// Table component for displaying labor summary by category

import React from 'react';
import { Users, Clock, AlertTriangle, CheckCircle } from 'lucide-react';
import type { LaborSummary } from '../../types/deliverables';

interface LaborSummaryTableProps {
  laborSummary: LaborSummary;
  className?: string;
}

export function LaborSummaryTable({ laborSummary, className = '' }: LaborSummaryTableProps) {
  if (!laborSummary || !laborSummary.by_category || laborSummary.by_category.length === 0) {
    return (
      <div className={`bg-bw-surface-subtle rounded-lg p-4 text-center text-bw-muted ${className}`}>
        No labor categories to display
      </div>
    );
  }

  const totalHours = laborSummary.total_hours || { min: 0, max: 0, recommended: 0, ceiling: undefined };
  const ceiling = totalHours.ceiling;
  const isOverCeiling = ceiling && totalHours.recommended > ceiling;

  return (
    <div className={`bg-bw-surface rounded-xl border border-bw-border overflow-hidden ${className}`}>
      <div className="bg-bw-surface-subtle px-4 py-3 border-b border-bw-border">
        <h3 className="font-semibold text-bw-ink flex items-center gap-2">
          <Users className="h-5 w-5 text-bw-primary" />
          Labor Summary by Category
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bw-surface-subtle">
            <tr>
              <th className="text-left p-3 font-medium text-bw-muted">Labor Category</th>
              <th className="text-center p-3 font-medium text-bw-muted">SCA Title</th>
              <th className="text-right p-3 font-medium text-bw-muted">Min Hours</th>
              <th className="text-right p-3 font-medium text-bw-muted">Max Hours</th>
              <th className="text-center p-3 font-medium text-bw-muted">SCA Applicable</th>
            </tr>
          </thead>
          <tbody>
            {laborSummary.by_category.map((category, index) => (
              <tr
                key={index}
                className="border-b border-bw-border last:border-0 hover:bg-bw-surface-subtle transition-colors"
              >
                <td className="p-3">
                  <span className="font-medium text-bw-ink">{category.category}</span>
                </td>
                <td className="p-3 text-center">
                  {category.sca_title ? (
                    <span className="inline-flex px-2 py-0.5 bg-bw-primary-tint text-bw-primary rounded text-xs">
                      {category.sca_title}
                    </span>
                  ) : (
                    <span className="text-bw-muted">-</span>
                  )}
                </td>
                <td className="p-3 text-right text-bw-muted">
                  {category.total_hours?.min?.toLocaleString() ?? '-'}
                </td>
                <td className="p-3 text-right text-bw-muted">
                  {category.total_hours?.max?.toLocaleString() ?? '-'}
                </td>
                <td className="p-3 text-center">
                  {category.sca_applicable ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-success-bg text-success rounded text-xs">
                      <CheckCircle className="h-3 w-3" />
                      Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-bw-surface-subtle text-bw-muted rounded text-xs">
                      No
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-100">
            <tr className="font-semibold">
              <td className="p-3 text-bw-ink">Total Hours</td>
              <td className="p-3"></td>
              <td className="p-3 text-right text-bw-ink">{totalHours.min.toLocaleString()}</td>
              <td className="p-3 text-right text-bw-ink">{totalHours.max.toLocaleString()}</td>
              <td className="p-3"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Summary Footer */}
      <div className="px-4 py-3 bg-bw-surface-subtle border-t border-bw-border">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-bw-primary" />
              <span className="text-sm text-bw-muted">Recommended:</span>
              <span className="font-bold text-bw-primary">{totalHours.recommended.toLocaleString()} hrs</span>
            </div>
            {ceiling && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-bw-muted">Ceiling:</span>
                <span className={`font-bold ${isOverCeiling ? 'text-urgent' : 'text-bw-ink'}`}>
                  {ceiling.toLocaleString()} hrs
                </span>
              </div>
            )}
          </div>

          {isOverCeiling && (
            <div className="flex items-center gap-2 text-urgent">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm font-medium">Exceeds ceiling by {(totalHours.recommended - ceiling).toLocaleString()} hrs</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LaborSummaryTable;
